import { useEffect, useRef } from 'react';

const SECTION_HIGHLIGHT_CLASS = 'section-highlight-fade';
const DEFAULT_HIGHLIGHT_COLOR = '#f0f9ff';

const POLL_INTERVAL_MS = 50;
const MAX_POLL_ATTEMPTS = 10;

function clearSectionHighlight(element: HTMLElement | null) {
    if (!element) return;
    element.classList.remove(SECTION_HIGHLIGHT_CLASS);
    element.style.removeProperty('--section-highlight-color');
}

export function useHighlightActiveSection(
    activeStepId?: string | null,
    options?: {
        scrollTrigger?: number;
        highlightColor?: string;
    },
) {
    const highlightedElementRef = useRef<HTMLElement | null>(null);
    const highlightColor = options?.highlightColor ?? DEFAULT_HIGHLIGHT_COLOR;
    const scrollTrigger = options?.scrollTrigger ?? 0;

    // The active step also changes when the user clicks a section in the
    // preview (to sync the sidebar); scrolling then would yank them to the
    // section's first fragment, possibly pages away. Only explicit navigation
    // (sidebar click, add section, any "ir para" surface — see
    // `handleSectionClick`'s `via` gate) bumps scrollTrigger and should scroll,
    // so the effect keys on the trigger alone and reads the rest from refs.
    const activeStepIdRef = useRef(activeStepId);
    activeStepIdRef.current = activeStepId;
    const highlightColorRef = useRef(highlightColor);
    highlightColorRef.current = highlightColor;

    useEffect(() => {
        if (scrollTrigger === 0) return;

        const stepId = activeStepIdRef.current;
        if (!stepId) return;

        const elementId = `section-${stepId}`;
        let animationFrameId: number | null = null;
        let pollCount = 0;
        let pollTimerId: ReturnType<typeof setTimeout> | null = null;

        function tryScrollAndHighlight() {
            const element = document.getElementById(elementId) as HTMLElement | null;

            if (element) {
                clearSectionHighlight(highlightedElementRef.current);

                element.scrollIntoView({ behavior: 'smooth', block: 'start' });

                element.style.setProperty('--section-highlight-color', highlightColorRef.current);
                element.classList.remove(SECTION_HIGHLIGHT_CLASS);

                animationFrameId = window.requestAnimationFrame(() => {
                    element.classList.add(SECTION_HIGHLIGHT_CLASS);
                });

                highlightedElementRef.current = element;
                return;
            }

            pollCount++;
            if (pollCount < MAX_POLL_ATTEMPTS) {
                pollTimerId = setTimeout(tryScrollAndHighlight, POLL_INTERVAL_MS);
            } else {
                // Fallback: scroll preview container to top
                const previewContainer = document.querySelector('[data-preview-container]');
                if (previewContainer) {
                    previewContainer.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        }

        // Small initial delay to let React commit state updates to the DOM
        pollTimerId = setTimeout(tryScrollAndHighlight, POLL_INTERVAL_MS);

        return () => {
            if (pollTimerId !== null) {
                clearTimeout(pollTimerId);
            }
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
            clearSectionHighlight(highlightedElementRef.current);
        };
    }, [scrollTrigger]);
}
